import { useState } from "react";
import { format, parseISO, addDays, addMonths, subDays, differenceInDays } from "date-fns";
import { Shield, Plus, CheckCircle2, Paperclip, Pencil, Trash2, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Progress } from "../ui/progress";
import { Chip } from "../ui/chip";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { DocThumb } from "../../pages/manage-post-disbursement/DocThumb";
import type { AssetInsuranceRecord } from "../../types/postDisbursement";
import {
  getPolicyTenureMonths,
  inrFmt,
  type SaveInsurancePolicyFormInput,
} from "../../lib/assetInsurance";
import { toast } from "react-toastify";

interface AssetInsurancePoliciesSectionProps {
  policies: AssetInsuranceRecord[];
  tenureMonths: number;
  coveredMonths: number;
  remainingMonths: number | null;
  loading?: boolean;
  onSavePolicy: (
    policy: SaveInsurancePolicyFormInput,
    policyFile?: File | null,
    quotationFile?: File | null,
    policyId?: string,
  ) => Promise<void>;
  onDeletePolicy?: (policyId: string) => Promise<void>;
}

export function AssetInsurancePoliciesSection({
  policies,
  tenureMonths,
  coveredMonths,
  remainingMonths,
  loading = false,
  onSavePolicy,
  onDeletePolicy,
}: AssetInsurancePoliciesSectionProps) {
  const tenureYears = Math.max(1, Math.ceil(tenureMonths / 12));
  const sortedAsc = policies.slice().sort((a, b) => a.start_date.localeCompare(b.start_date));
  const effectiveRemaining = remainingMonths ?? Math.max(0, tenureMonths - coveredMonths);
  const coveragePct = tenureMonths > 0
    ? Math.min(100, Math.round((coveredMonths / tenureMonths) * 100))
    : 0;

  const [showAddPolicy, setShowAddPolicy] = useState(false);
  const [pStart, setPStart] = useState(format(new Date(), "yyyy-MM-dd"));
  const [pTenure, setPTenure] = useState<string>("12");
  const computeEnd = (start: string, months: number) =>
    format(subDays(addMonths(parseISO(start), months), 1), "yyyy-MM-dd");
  const pEnd = pStart && pTenure ? computeEnd(pStart, Number(pTenure) || 12) : "";
  const [pProvider, setPProvider] = useState("");
  const [pPremium, setPPremium] = useState("");
  const [pFile, setPFile] = useState<File | null>(null);
  const [pQuote, setPQuote] = useState<File | null>(null);
  const [pCoordName, setPCoordName] = useState("");
  const [pCoordEmail, setPCoordEmail] = useState("");
  const [pCoordPhone, setPCoordPhone] = useState("");
  const [showCoord, setShowCoord] = useState(false);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<AssetInsuranceRecord | null>(null);
  const [deletingPolicyId, setDeletingPolicyId] = useState<string | null>(null);
  const [policyPendingDelete, setPolicyPendingDelete] = useState<{
    policy: AssetInsuranceRecord;
    yearNumber: number;
  } | null>(null);
  const editingPolicyMonths = editingPolicy ? getPolicyTenureMonths(editingPolicy) : 0;
  const maxTenureMonths = editingPolicy
    ? effectiveRemaining + editingPolicyMonths
    : effectiveRemaining;
  const tenureMonthsEntered = Number(pTenure) || 0;
  const tenureExceedsMax = maxTenureMonths > 0 && pTenure !== "" && tenureMonthsEntered > maxTenureMonths;

  const resetPolicyForm = () => {
    setPStart(format(new Date(), "yyyy-MM-dd"));
    setPTenure("12");
    setPProvider("");
    setPPremium("");
    setPFile(null);
    setPQuote(null);
    setPCoordName("");
    setPCoordEmail("");
    setPCoordPhone("");
    setEditingPolicy(null);
    setShowCoord(false);
  };

  const startEditPolicy = (policy: AssetInsuranceRecord) => {
    setEditingPolicy(policy);
    setPStart(policy.start_date);
    setPTenure(String(getPolicyTenureMonths(policy)));
    setPProvider(policy.insurance_provider);
    setPPremium(String(policy.premium));
    setPFile(null);
    setPQuote(null);
    const hasCoord = !!(policy.coordinator_name || policy.coordinator_email || policy.coordinator_phone);
    setShowCoord(hasCoord);
    setPCoordName(policy.coordinator_name ?? "");
    setPCoordEmail(policy.coordinator_email ?? "");
    setPCoordPhone(policy.coordinator_phone ?? "");
    setShowAddPolicy(true);
  };

  const handleDeletePolicy = async (policy: AssetInsuranceRecord) => {
    if (!policy.id || !onDeletePolicy) return;
    setDeletingPolicyId(policy.id);
    try {
      await onDeletePolicy(policy.id);
      setPolicyPendingDelete(null);
      if (editingPolicy?.id === policy.id) {
        setShowAddPolicy(false);
        resetPolicyForm();
      }
    } catch {
      // Error toast handled by caller.
    } finally {
      setDeletingPolicyId(null);
    }
  };

  const latestEndISO = sortedAsc.length > 0 ? sortedAsc[sortedAsc.length - 1].end_date : null;
  const defaultStart = latestEndISO
    ? format(addDays(parseISO(latestEndISO), 1), "yyyy-MM-dd")
    : format(new Date(), "yyyy-MM-dd");
  const fullyCovered = tenureMonths > 0 && effectiveRemaining === 0;

  const openForm = () => {
    resetPolicyForm();
    setPStart(defaultStart);
    const def = effectiveRemaining >= 12 ? "12" : String(Math.max(1, effectiveRemaining));
    setPTenure(def);
    setShowAddPolicy(true);
  };

  const handleSave = async () => {
    if (maxTenureMonths > 0 && tenureMonthsEntered > maxTenureMonths) {
      toast.error(
        `Maximum tenure allowed is ${maxTenureMonths} months. You entered ${tenureMonthsEntered} months.`,
      );
      return;
    }
    setSavingPolicy(true);
    try {
      await onSavePolicy(
        {
          start_date: pStart,
          end_date: pEnd,
          tenure_months: Number(pTenure) || undefined,
          insurance_provider: pProvider,
          premium: Number(pPremium) || 0,
          coordinator_name: pCoordName || undefined,
          coordinator_email: pCoordEmail || undefined,
          coordinator_phone: pCoordPhone || undefined,
        },
        pFile,
        pQuote,
        editingPolicy?.id,
      );
      setShowAddPolicy(false);
      resetPolicyForm();
    } catch {
      // Error toast handled by caller.
    } finally {
      setSavingPolicy(false);
    }
  };

  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between mb-3 gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-rose-50">
              <Shield className="h-4 w-4 text-rose-500" />
            </div>
            <h3 className="text-[13px] font-semibold text-slate-700">Insurance Policies</h3>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            <Chip className="bg-blue-500/10 text-blue-700 text-[10px] px-2 py-0.5">
              Lease Tenure: {tenureMonths} mo ({tenureYears}y)
            </Chip>
            <Chip className="bg-muted text-foreground text-[10px] px-2 py-0.5">
              Covered: {coveredMonths} / {tenureMonths} mo
            </Chip>
            {fullyCovered ? (
              <Chip className="bg-emerald-500/10 text-emerald-700 text-[10px] px-2 py-0.5">Fully covered</Chip>
            ) : (
              <Chip className="bg-amber-500/10 text-amber-700 text-[10px] px-2 py-0.5">
                Remaining: {effectiveRemaining} mo
              </Chip>
            )}
          </div>
        </div>
        {!showAddPolicy && !fullyCovered && (
          <Button
            size="sm"
            className="gap-1 shrink-0 h-7 px-3 text-[11px]"
            onClick={openForm}
            disabled={loading}
          >
            <Plus className="h-3.5 w-3.5" />Add Policy
          </Button>
        )}
      </div>

      <Progress value={coveragePct} className="h-2 mb-4" />

      {loading && (
        <div className="rounded-lg border border-dashed p-4 mb-4 text-center text-xs text-slate-500">
          Loading insurance policies…
        </div>
      )}

      {fullyCovered && !showAddPolicy && (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 mb-4 text-xs text-emerald-700 flex items-center gap-2">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Asset is fully insured for the {tenureYears}-year lease tenure.
        </div>
      )}

      {showAddPolicy && (
        <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 mb-4 space-y-4">
          {editingPolicy && (
            <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Edit Policy</div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-[10px] font-semibold text-slate-400">Start Date</Label>
              <Input type="date" value={pStart} onChange={(e) => setPStart(e.target.value)} className="h-9 text-[12px]" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-semibold text-slate-400">Tenure (months)</Label>
              <Input
                inputMode="numeric"
                pattern="[0-9]*"
                className={`h-9 text-[12px]${tenureExceedsMax ? " border-destructive focus-visible:ring-destructive" : ""}`}
                value={pTenure}
                onChange={(e) => setPTenure(e.target.value.replace(/\D/g, ""))}
                placeholder={`e.g. ${Math.min(12, maxTenureMonths) || 12}`}
              />
              {tenureExceedsMax ? (
                <p className="text-[11px] text-destructive mt-1">
                  Maximum allowed is {maxTenureMonths} months. You entered {tenureMonthsEntered} months.
                </p>
              ) : maxTenureMonths > 0 ? (
                <p className="text-[11px] text-slate-400 mt-1">Max {maxTenureMonths} mo remaining</p>
              ) : null}
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-semibold text-slate-400">End Date (auto)</Label>
              <Input type="date" value={pEnd} readOnly className="h-9 text-[12px] bg-slate-100" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-[10px] font-semibold text-slate-400">Provider</Label>
              <Input
                value={pProvider}
                onChange={(e) => setPProvider(e.target.value)}
                placeholder="e.g. ICICI Lombard"
                className="h-9 text-[12px]"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-semibold text-slate-400">Premium (₹)</Label>
              <Input
                type="number"
                value={pPremium}
                onChange={(e) => setPPremium(e.target.value)}
                className="h-9 text-[12px]"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-[10px] font-semibold text-slate-400">Upload Policy (PDF)</Label>
              <Input type="file" accept=".pdf" onChange={(e) => setPFile(e.target.files?.[0] || null)} className="text-[12px]" />
              {pFile && (
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                  <Paperclip className="h-3 w-3" />{pFile.name}
                </div>
              )}
              {!pFile && editingPolicy?.upload_policy_s3_fileurl && (
                <div className="text-xs text-slate-500 mt-1">
                  Current: {editingPolicy.upload_policy_filename || "Policy file"}
                </div>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-semibold text-slate-400">Upload Quotation (PDF/XLSX)</Label>
              <Input type="file" accept=".pdf,.xlsx,.xls" onChange={(e) => setPQuote(e.target.files?.[0] || null)} className="text-[12px]" />
              {pQuote && (
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                  <Paperclip className="h-3 w-3" />{pQuote.name}
                </div>
              )}
              {!pQuote && editingPolicy?.upload_quotation_s3_fileurl && (
                <div className="text-xs text-slate-500 mt-1">
                  Current: {editingPolicy.upload_quotation_filename || "Quotation file"}
                </div>
              )}
            </div>
          </div>

          {!showCoord ? (
            <button
              type="button"
              onClick={() => setShowCoord(true)}
              className="text-xs text-[#3f8b98] hover:underline inline-flex items-center gap-1"
            >
              <Plus className="h-3 w-3" />Add Coordinator Contact (optional)
            </button>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                  Coordinator Contact (optional)
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowCoord(false);
                    setPCoordName("");
                    setPCoordEmail("");
                    setPCoordPhone("");
                  }}
                  className="text-[11px] text-slate-400 hover:text-slate-600"
                >
                  Remove
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-[10px] font-semibold text-slate-400">Name</Label>
                  <Input value={pCoordName} onChange={(e) => setPCoordName(e.target.value)} placeholder="Contact name" className="h-9 text-[12px]" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-semibold text-slate-400">Email</Label>
                  <Input type="email" value={pCoordEmail} onChange={(e) => setPCoordEmail(e.target.value)} placeholder="email@insurer.com" className="h-9 text-[12px]" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-semibold text-slate-400">Phone</Label>
                  <Input value={pCoordPhone} onChange={(e) => setPCoordPhone(e.target.value)} placeholder="+91…" className="h-9 text-[12px]" />
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setShowAddPolicy(false);
                resetPolicyForm();
                setShowCoord(false);
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-[#3f8b98] hover:bg-[#3f8b98]/90"
              disabled={!pProvider || !pPremium || !pStart || !pEnd || !pTenure || savingPolicy || Number(pTenure) < 1}
              onClick={() => void handleSave()}
            >
              {savingPolicy ? "Saving…" : editingPolicy ? "Update Policy" : "Add Policy"}
            </Button>
          </div>
        </div>
      )}

      {!loading && sortedAsc.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-[12px] text-slate-500">
          No insurance policies added yet.
        </div>
      ) : !loading ? (
        <ul className="space-y-2">
          {sortedAsc.map((p, idx) => {
            const end = parseISO(p.end_date);
            const remain = differenceInDays(end, new Date());
            const remainTone = remain < 0
              ? "bg-red-500/10 text-red-700"
              : remain <= 30
                ? "bg-amber-500/10 text-amber-700"
                : "bg-emerald-500/10 text-emerald-700";
            const hasCoord = p.coordinator_email || p.coordinator_phone || p.coordinator_name;
            const isLatest = idx === sortedAsc.length - 1;
            const renewableFrom = isLatest && effectiveRemaining > 0 ? format(addDays(end, 1), "dd MMM yyyy") : null;
            return (
              <li key={p.id} className="rounded-xl border border-slate-100 p-3 flex items-start justify-between gap-3 bg-slate-50/50">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Chip className="bg-primary/10 text-primary text-[10px] px-2 py-0.5">
                      Year {idx + 1} of {tenureYears}
                    </Chip>
                    <span className="text-[12px] font-semibold text-slate-700">{p.insurance_provider}</span>
                    {p.tenure_months && (
                      <Chip className="bg-muted text-muted-foreground text-[10px] px-2 py-0.5">
                        {p.tenure_months} mo
                      </Chip>
                    )}
                    <Chip className={`text-[10px] px-2 py-0.5 ${remainTone}`}>
                      {remain < 0 ? `${Math.abs(remain)}d expired` : `${remain}d left`}
                    </Chip>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    {format(parseISO(p.start_date), "dd MMM yyyy")} → {format(end, "dd MMM yyyy")} · Premium {inrFmt(p.premium)}
                  </div>
                  {renewableFrom && (
                    <div className="text-[11px] text-amber-700 mt-1">
                      Renewable from {renewableFrom} · {effectiveRemaining} mo remaining
                    </div>
                  )}
                  {hasCoord && (
                    <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-x-3">
                      {p.coordinator_name && <span>👤 {p.coordinator_name}</span>}
                      {p.coordinator_email && <span>📧 {p.coordinator_email}</span>}
                      {p.coordinator_phone && <span>📞 {p.coordinator_phone}</span>}
                    </div>
                  )}
                  {(p.upload_policy_s3_fileurl || p.upload_quotation_s3_fileurl) && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3 max-w-[320px]">
                      {p.upload_policy_s3_fileurl && (
                        <div>
                          <DocThumb name={p.upload_policy_filename || "Policy"} url={p.upload_policy_s3_fileurl} />
                          <div className="text-[10px] text-slate-400 mt-0.5 text-center">Policy</div>
                        </div>
                      )}
                      {p.upload_quotation_s3_fileurl && (
                        <div>
                          <DocThumb name={p.upload_quotation_filename || "Quotation"} url={p.upload_quotation_s3_fileurl} />
                          <div className="text-[10px] text-slate-400 mt-0.5 text-center">Quotation</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => startEditPolicy(p)}
                    aria-label="Edit policy"
                    className="h-8 w-8 p-0"
                    disabled={deletingPolicyId === p.id}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  {onDeletePolicy && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      aria-label="Delete policy"
                      disabled={deletingPolicyId === p.id || savingPolicy}
                      onClick={() => setPolicyPendingDelete({ policy: p, yearNumber: idx + 1 })}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-red-600" />
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}

      <Dialog
        open={!!policyPendingDelete}
        onOpenChange={(open) => {
          if (!open && !deletingPolicyId) setPolicyPendingDelete(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete insurance policy?</DialogTitle>
          </DialogHeader>
          {policyPendingDelete && (
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete the insurance policy for{" "}
              <span className="font-medium text-foreground">
                {policyPendingDelete.policy.insurance_provider}
              </span>{" "}
              (Year {policyPendingDelete.yearNumber} of {tenureYears})? This action cannot be undone.
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPolicyPendingDelete(null)}
              disabled={!!deletingPolicyId}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="border-transparent bg-red-600 text-white hover:bg-red-700 hover:text-white focus-visible:ring-red-600"
              disabled={!policyPendingDelete || !!deletingPolicyId}
              onClick={() =>
                policyPendingDelete && void handleDeletePolicy(policyPendingDelete.policy)
              }
            >
              {deletingPolicyId === policyPendingDelete?.policy.id ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Deleting…
                </>
              ) : (
                "OK"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
